import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  updateAppointmentStatus,
  updateAppointmentActionNotes,
  undoCampaignDecline,
  updateAppointment,
} from "@services/appointments";
import {
  CALL_TAG_OPTIONS,
  CALL_TAG_CONFIG,
  UNTAGGED_CONFIG,
  getCallTagConfig,
} from "@constants/callTags";
import { DEFAULT_CALLS_QUERY } from "@constants/callsQuery";
import ActionNotesModal from "./ActionNotesModal";
import EditAppointmentModal from "./EditAppointmentModal";
import AudioPlayButton from "./AudioPlayButton";
import CallRatingModal from "./CallRatingModal";
import { OutboundCallButton, CallOutcomeInline, CallSummaryModal } from "../outbound";

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

const formatPreferredDate = (dateString) => {
  // Handle pre-formatted date string from backend
  // Formats: "Feb 12, 03:00 PM" or "Feb 10, 09:00 AM -> Feb 15, 05:00 PM"
  if (!dateString || typeof dateString !== "string") {
    return "—";
  }

  try {
    // Check if it's a range (contains "->")
    if (dateString.includes("->")) {
      const [startPart, endPart] = dateString.split("->").map((s) => s.trim());

      // Return with bold "Anytime:" and "to"
      return (
        <>
          <strong>Anytime:</strong> {startPart} <strong>to</strong> {endPart}
        </>
      );
    }

    // Single date/time - return as-is
    return dateString;
  } catch {
    return dateString || "—";
  }
};

const STATUS_OPTIONS = [
  "NEW_CALL",
  "PENDING",
  "HANDLED",
  "RESOLVED",
  "DISCARD",
];

const STATUS_BASE_CLASSES =
  "inline-flex items-center justify-center gap-1.5 rounded-full px-3 text-sm font-medium whitespace-nowrap transition-all duration-200 h-7 min-w-[80px] hover:shadow-sm cursor-pointer";

const STATUS_CONFIG = {
  NEW_CALL: {
    label: "New Call",
    classes: "bg-emerald-100 text-emerald-800",
    dot: "bg-emerald-500",
  },
  PENDING: {
    label: "Pending",
    classes: "bg-amber-100 text-amber-800",
    dot: "bg-amber-500",
  },
  HANDLED: {
    label: "Handled",
    classes: "bg-sky-100 text-sky-800",
    dot: "bg-sky-500",
  },
  RESOLVED: {
    label: "Resolved",
    classes: "bg-violet-100 text-violet-800",
    dot: "bg-violet-500",
  },
  DISCARD: {
    label: "Discard",
    classes: "bg-rose-100 text-rose-800",
    dot: "bg-rose-500",
  },
  HANDLED_BY_AI: {
    label: "Handled by AI",
    classes: "bg-indigo-100 text-indigo-800",
    dot: "bg-indigo-500",
  },
};

const formatPhoneDisplay = (phone) => {
  if (!phone) return "—";
  return phone;
};

const normalizePhone = (phone) => String(phone || "").replace(/\D/g, "");

// Tolerates formatting and country-code/trunk-prefix differences
// (e.g. "+61 2 1234 5678" vs "02 1234 5678") by comparing the last 8 digits.
const isSamePhone = (a, b) => {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return na.length >= 8 && nb.length >= 8 && na.slice(-8) === nb.slice(-8);
};

const getStatusClasses = (status) => {
  const normalized = (status || "").toUpperCase();
  const config = STATUS_CONFIG[normalized] || STATUS_CONFIG.NEW_CALL;
  return STATUS_BASE_CLASSES + " " + config.classes;
};

const getStatusDot = (status) => {
  const normalized = (status || "").toUpperCase();
  return STATUS_CONFIG[normalized]?.dot || STATUS_CONFIG.NEW_CALL.dot;
};

const getStatusLabel = (status) => {
  const normalized = (status || "").toUpperCase();
  return STATUS_CONFIG[normalized]?.label || STATUS_CONFIG.NEW_CALL.label;
};

/**
 * ✅ New users (0 rows) should see these columns exactly as today.
 */
const DEFAULT_COLUMNS = [
  { key: "phone_number", label: "Phone Number" },
  { key: "patient_name", label: "Name" },
  { key: "call_tag", label: "Call Tag", isCallTag: true },
  { key: "reason_for_visit", label: "Call Summary" },
  { key: "session_type", label: "Session Type" },
  { key: "insurance_provider", label: "Insurance" },
  { key: "email", label: "Email" },
  { key: "date", label: "Preferred Date" },
  { key: "created_at", label: "Call Time" },
  { key: "recording", label: "Recording", isRecording: true },
  { key: "outbound", label: "Confirm Call", isOutbound: true },
  { key: "action_notes", label: "Notes", isActionNotes: true },
  { key: "status", label: "Status", isStatus: true },
];

const ALWAYS_KEEP_KEYS = new Set([
  "status",
  "action_notes",
  "recording",
  "call_tag",
  "outbound",
]);

const HIDDEN_KEYS = new Set([
  "id",
  "preferred_timezone",
  "date_ranges", // Raw JSON, formatted via "date" column
  "has_recording", // Used internally by Recording column
  "recording_pending", // Used internally by Recording column
  "last_outbound_call_at", // Used internally by Confirm Call column
  "outbound_call_outcome", // Used internally by Confirm Call column
  "outbound_call_outcome_label", // Used internally by Confirm Call column
  "booking_source", // Rendered via the dedicated "Source" badge column
  "elevenlabs_conversation_url", // Rendered via the dedicated "Conversation" column
]);

const shouldHideKey = (key) => {
  if (!key) return true;
  const k = String(key);
  return HIDDEN_KEYS.has(k) || /_id$/i.test(k);
};

const isTrulyEmpty = (val) => {
  if (val === null || val === undefined) return true;
  if (typeof val === "string") return val.trim().length === 0;
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === "object") return Object.keys(val).length === 0;
  return false; // numbers/booleans count as data
};

// The API occasionally grows nested object/array fields; rendering those
// directly crashes React ("Objects are not valid as a React child").
const toDisplayText = (val) => {
  if (isTrulyEmpty(val)) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return "";
};

const hasAnyDataInColumn = (rows, key) =>
  rows.some((r) => !isTrulyEmpty(r?.[key]));

const getRowId = (appt) => appt?.id || appt?.appointment_id;

const CUSTOM_LABELS = {
  patient_name: "Name",
  created_at: "Call Time",
  date: "Preferred Date",
  reason_for_visit: "Call Summary",
  action_notes: "Notes",
  insurance_provider: "Insurance",
};

const toTitle = (key) => {
  if (CUSTOM_LABELS[key]) return CUSTOM_LABELS[key];
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const PREFERRED_ORDER = [
  "phone_number",
  "callback_phone_number",
  "patient_name",
  "source",
  "call_tag",
  "reason_for_visit",
  "session_type",
  "insurance_provider",
  "email",
  "date",
  "created_at",
  "updated_at",
  "recording",
  "outbound",
  "rating",
  "edit",
  "action_notes",
  "status",
];

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_ROWS = [];

const sumCounts = (obj) =>
  Object.values(obj || {}).reduce((acc, n) => acc + (Number(n) || 0), 0);

// An appointment the outreach dialer booked. The backend wrote "INTAKE"
// before the pipeline was renamed and "OUTREACH" after; both are the same
// provenance.
const OUTREACH_SOURCES = new Set(["INTAKE", "OUTREACH"]);
const isOutreachBooking = (appt) => (
  OUTREACH_SOURCES.has(String(appt?.booking_source || "").toUpperCase())
);

export default function BookedAppointmentsTable({
  // `rows` is the paged list the parent loaded; `appointments` is the older
  // name for the same thing and still works.
  rows: rowsProp,
  appointments = EMPTY_ROWS,
  loading = false,
  error = null,
  onRetry = () => {},
  // Chip counts for the whole set under the current search, from the server.
  // Without them the chips count the rows on screen.
  counts = null,
  // Controlled query state. Without `query` the table keeps its own copy, so
  // a table rendered with rows alone still works.
  query,
  onQueryChange,
  // Paging, from useInfiniteList. Without a sentinelRef the table shows the
  // rows it has and nothing else.
  loadingMore = false,
  hasMore = false,
  total = null,
  onLoadMore,
  sentinelRef,
  scrollRootRef,
  // An optimistic patch is applied here and reported up, so the parent's
  // list (which replaces this one when a page lands) carries it too.
  onRowPatch,

  isAdmin = false,
  // Admin only: the selected clinic's own number, from the shared scope.
  clinicPhone = "",
  outboundEnabled = false,
}) {
  const parentRows = rowsProp ?? appointments;
  const [rows, setRows] = useState(parentRows);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);
  // Spec §15.2: a saved note can stop campaign calls to the patient's
  // number. {active, undone} drives the one line under the textarea.
  const [campaignDecline, setCampaignDecline] = useState(null);
  const [undoingDecline, setUndoingDecline] = useState(false);
  const [openCallTagMenuId, setOpenCallTagMenuId] = useState(null);
  const [updatingCallTagId, setUpdatingCallTagId] = useState(null);
  const [callTagError, setCallTagError] = useState(null);

  // Query state: the parent's when controlled, else a local copy.
  const [localQuery, setLocalQuery] = useState(DEFAULT_CALLS_QUERY);
  const activeQuery = query ?? localQuery;
  const activeQueryRef = useRef(activeQuery);
  activeQueryRef.current = activeQuery;
  const controlled = query !== undefined;
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const changeQuery = useCallback(
    (patch) => {
      const next = { ...activeQueryRef.current, ...patch };
      if (controlled) onQueryChangeRef.current?.(next);
      else setLocalQuery(next);
    },
    [controlled],
  );
  const statusFilter = activeQuery.status || "ALL";
  const callTagFilter = activeQuery.callTag || "ALL";
  const sortDirection = activeQuery.sort === "asc" ? "asc" : "desc";
  const setStatusFilter = (value) =>
    changeQuery({ status: value === "ALL" ? null : value });
  const setCallTagFilter = (value) =>
    changeQuery({ callTag: value === "ALL" ? null : value });

  // The search box keeps what is typed and sends it on after a pause.
  const [searchText, setSearchText] = useState(activeQuery.search || "");
  const appliedSearch = activeQuery.search || "";
  useEffect(() => {
    if (searchText.trim() === appliedSearch) return undefined;
    const id = setTimeout(
      () => changeQuery({ search: searchText.trim() }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [searchText, appliedSearch, changeQuery]);
  const clearSearch = () => {
    setSearchText("");
    changeQuery({ search: "" });
  };
  const clearFilters = () => {
    setSearchText("");
    changeQuery({ search: "", status: null, callTag: null });
  };
  const isFiltered = Boolean(
    appliedSearch || activeQuery.status || activeQuery.callTag,
  );

  // Rating modal state (admin only)
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingModalData, setRatingModalData] = useState({
    conversationId: null,
    patientName: null,
  });

  // Edit modal state (admin only)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // Call summary modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryCallId, setSummaryCallId] = useState(null);
  const [summaryAppointment, setSummaryAppointment] = useState(null);

  // Call summary expansion state
  const [expandedSummaryRowId, setExpandedSummaryRowId] = useState(null);

  // The parent's list already holds every page loaded; a new identity means
  // a page landed or a reload replaced it, and the local copy follows.
  useEffect(() => {
    setRows(parentRows);
  }, [parentRows]);

  const patchRow = (id, patch) => {
    setRows((prev) =>
      prev.map((r) => (getRowId(r) === id ? { ...r, ...patch } : r)),
    );
    onRowPatch?.(id, patch);
  };
  // The fields a patch touched, as they were, so a failed save can undo it.
  const revertFor = (id, patch) => {
    const before = rows.find((r) => getRowId(r) === id) || {};
    return Object.fromEntries(Object.keys(patch).map((k) => [k, before[k]]));
  };

  // Admin only: the selected clinic's own phone ("Clinic Phone (original)"),
  // used to flag calls that appear to originate from the clinic itself.
  const selectedClinicPhone = isAdmin ? clinicPhone : "";

  const toggleSort = () => {
    changeQuery({ sort: sortDirection === "asc" ? "desc" : "asc" });
  };

  // Search, filters and sort are the server's job: the rows arrive ready.
  const filteredRows = rows;

  // Chip counts: the server's for the whole set, else counted on screen.
  const statusCounts = useMemo(() => {
    if (counts?.status) {
      const fromServer = { ALL: sumCounts(counts.status) };
      STATUS_OPTIONS.forEach((status) => {
        fromServer[status] = Number(counts.status[status]) || 0;
      });
      return fromServer;
    }
    const derived = { ALL: rows.length };
    STATUS_OPTIONS.forEach((status) => {
      derived[status] = rows.filter(
        (row) => (row.status || "NEW_CALL").toUpperCase() === status,
      ).length;
    });
    return derived;
  }, [rows, counts]);

  const callTagCounts = useMemo(() => {
    if (counts?.call_tag) {
      const fromServer = { ALL: sumCounts(counts.call_tag), UNTAGGED: 0 };
      Object.entries(counts.call_tag).forEach(([tag, n]) => {
        fromServer[String(tag).toUpperCase()] = Number(n) || 0;
      });
      return fromServer;
    }
    const derived = { ALL: rows.length, UNTAGGED: 0 };
    CALL_TAG_OPTIONS.forEach((tag) => {
      derived[tag] = 0;
    });
    rows.forEach((row) => {
      const tag = row.call_tag ? String(row.call_tag).toUpperCase() : null;
      if (!tag) {
        derived.UNTAGGED += 1;
      } else if (derived[tag] !== undefined) {
        derived[tag] += 1;
      }
    });
    return derived;
  }, [rows, counts]);

  const columns = useMemo(() => {
    // Determine which keys to always keep (conditionally exclude outbound if disabled)
    const keysToAlwaysKeep = new Set(ALWAYS_KEEP_KEYS);
    if (!outboundEnabled) {
      keysToAlwaysKeep.delete("outbound");
    }

    // ✅ New user: show default columns (even if empty table)
    let baseCols;
    if (!Array.isArray(rows) || rows.length === 0) {
      baseCols = [...DEFAULT_COLUMNS];
      // Remove outbound column if disabled
      if (!outboundEnabled) {
        baseCols = baseCols.filter((c) => c.key !== "outbound");
      }
    } else {
      // ✅ After first row exists: dynamic keys, no IDs, hide empty columns (except status/notes)
      const keySet = new Set();
      for (const r of rows) {
        if (!r) continue;
        Object.keys(r).forEach((k) => {
          if (shouldHideKey(k)) return;
          // Skip outbound key if disabled
          if (k === "outbound" && !outboundEnabled) return;
          keySet.add(k);
        });
      }

      keysToAlwaysKeep.forEach((k) => keySet.add(k));

      baseCols = Array.from(keySet).map((k) => {
        if (k === "status") return { key: k, label: "Status", isStatus: true };
        if (k === "action_notes")
          return { key: k, label: "Notes", isActionNotes: true };
        if (k === "recording")
          return { key: k, label: "Recording", isRecording: true };
        if (k === "call_tag")
          return { key: k, label: "Call Tag", isCallTag: true };
        if (k === "outbound")
          return { key: k, label: "Confirm Call", isOutbound: true };
        return { key: k, label: toTitle(k) };
      });

      baseCols = baseCols.filter((c) => {
        if (keysToAlwaysKeep.has(c.key)) return true;
        return hasAnyDataInColumn(rows, c.key);
      });
    }

    // Add a "Source" badge column only when at least one row was booked by
    // the outreach dialer — keeps the column out of the way for clinics not
    // using it.
    const hasOutreachSource = rows.some((r) => isOutreachBooking(r));
    if (hasOutreachSource && !baseCols.some((c) => c.key === "source")) {
      baseCols.push({ key: "source", label: "Source", isSource: true });
    }

    // Add admin-only columns
    if (isAdmin) {
      baseCols.push({ key: "rating", label: "Rating", isRating: true });
      baseCols.push({ key: "edit", label: "Edit", isEdit: true });
      baseCols.push({
        key: "elevenlabs",
        label: "Conversation",
        isElevenLabsLink: true,
      });
    }

    const orderIndex = new Map(PREFERRED_ORDER.map((k, i) => [k, i]));
    baseCols.sort((a, b) => {
      const ai = orderIndex.has(a.key) ? orderIndex.get(a.key) : Infinity;
      const bi = orderIndex.has(b.key) ? orderIndex.get(b.key) : Infinity;
      if (ai !== bi) return ai - bi;
      return String(a.label).localeCompare(String(b.label));
    });

    return baseCols;
  }, [rows, isAdmin, outboundEnabled]);

  const hasData = Array.isArray(filteredRows) && filteredRows.length > 0;

  const handleStatusChange = async (appointment, newStatus) => {
    const id = getRowId(appointment);
    if (!id || newStatus === appointment.status) {
      setOpenMenuId(null);
      return;
    }

    setStatusError(null);
    setUpdatingId(id);
    setOpenMenuId(null);

    const patch = { status: newStatus };
    const revert = revertFor(id, patch);
    patchRow(id, patch);

    try {
      await updateAppointmentStatus(id, newStatus);
    } catch (err) {
      patchRow(id, revert);
      setStatusError(
        err?.message || "Failed to update status. Please try again.",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCallTagChange = async (appointment, newTag) => {
    const id = getRowId(appointment);
    if (!id) {
      setOpenCallTagMenuId(null);
      return;
    }
    const currentTag = appointment.call_tag || null;
    if (newTag === currentTag) {
      setOpenCallTagMenuId(null);
      return;
    }

    setCallTagError(null);
    setUpdatingCallTagId(id);
    setOpenCallTagMenuId(null);

    const patch = { call_tag: newTag };
    const revert = revertFor(id, patch);
    patchRow(id, patch);

    try {
      await updateAppointment(id, { call_tag: newTag });
    } catch (err) {
      patchRow(id, revert);
      setCallTagError(
        err?.message || "Failed to update call tag. Please try again.",
      );
    } finally {
      setUpdatingCallTagId(null);
    }
  };

  const handleOpenNotesModal = (appointment) => {
    setSelectedAppointment(appointment);
    setNotesModalOpen(true);
    setNotesError(null);
    // An earlier note already stopped the calls: offer Undo from the start.
    setCampaignDecline(
      appointment?.campaign_decline_id ? { active: true, undone: false } : null,
    );
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setSelectedAppointment(null);
    setNotesError(null);
    setCampaignDecline(null);
  };

  const handleSaveNotes = async (notes) => {
    const id = getRowId(selectedAppointment);
    if (!id) return;

    setSavingNotes(true);
    setNotesError(null);

    const patch = { action_notes: notes };
    const revert = revertFor(id, patch);
    patchRow(id, patch);

    try {
      const result = await updateAppointmentActionNotes(id, notes);
      const decline = result?.data?.campaign_decline;
      if (decline?.active && decline.id) {
        // The note stopped campaign calls to this number: stay open so the
        // line under the textarea can say so, and offer Undo.
        patchRow(id, { campaign_decline_id: decline.id });
        setCampaignDecline({ active: true, undone: false });
      } else {
        handleCloseNotesModal();
      }
    } catch (err) {
      patchRow(id, revert);
      setNotesError(err?.message || "Failed to save notes. Please try again.");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUndoCampaignDecline = async () => {
    const id = getRowId(selectedAppointment);
    if (!id) return;

    setUndoingDecline(true);
    setNotesError(null);

    try {
      await undoCampaignDecline(id);
      patchRow(id, { campaign_decline_id: null });
      setCampaignDecline({ active: false, undone: true });
    } catch (err) {
      setNotesError(
        err?.message || "Failed to resume campaign calls. Please try again.",
      );
    } finally {
      setUndoingDecline(false);
    }
  };

  // Rating modal handlers (admin only)
  const handleOpenRatingModal = (appointment) => {
    setRatingModalData({
      conversationId: appointment.conversation_id,
      patientName: appointment.patient_name,
    });
    setRatingModalOpen(true);
  };

  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setRatingModalData({ conversationId: null, patientName: null });
  };

  // Edit modal handlers (admin only)
  const handleOpenEditModal = (appt) => {
    setEditAppointment(appt);
    setEditModalOpen(true);
    setEditError(null);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditAppointment(null);
    setEditError(null);
  };

  const handleSaveEdit = async (changedFields) => {
    const id = getRowId(editAppointment);
    if (!id || Object.keys(changedFields).length === 0) {
      handleCloseEditModal();
      return;
    }

    setSavingEdit(true);
    setEditError(null);

    const revert = revertFor(id, changedFields);
    patchRow(id, changedFields);

    try {
      await updateAppointment(id, changedFields);
      handleCloseEditModal();
    } catch (err) {
      patchRow(id, revert);
      setEditError(err?.message || "Failed to save changes. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <section className="flex flex-col flex-1 min-h-0 min-w-0 w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-[#3d2398] to-[#2530a7] shadow-lg shadow-[#3d2398]/25">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Frontdesk Calls
              </h2>
              <p className="text-sm text-gray-500">
                Manage and track incoming calls
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-gray-900/25 transition-all duration-200 hover:bg-gray-800 hover:shadow-xl hover:shadow-gray-900/30 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0"
            onClick={onRetry}
            disabled={loading}
          >
            <svg
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by name, phone, or reason..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full h-11 pl-11 pr-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
          />
          {searchText && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Status Filter Chips */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setStatusFilter("ALL")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
              statusFilter === "ALL"
                ? "bg-gray-900 text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                statusFilter === "ALL" ? "bg-white/20" : "bg-gray-200"
              }`}
            >
              {statusCounts.ALL}
            </span>
          </button>
          {STATUS_OPTIONS.filter((status) => status !== "NEW_CALL").map(
            (status) => {
              const config = STATUS_CONFIG[status];
              const isActive = statusFilter === status;
              const count = statusCounts[status] || 0;

              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(isActive ? "ALL" : status)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                    isActive
                      ? `${config.classes} shadow-md`
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${isActive ? config.dot : "bg-gray-400"}`}
                  ></span>
                  {config.label}
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      isActive ? "bg-black/10" : "bg-gray-200"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            },
          )}
        </div>

        {/* Call Tag filter — compact dropdown */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="call-tag-filter"
            className="text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap"
          >
            Call Tag
          </label>
          <select
            id="call-tag-filter"
            value={callTagFilter}
            onChange={(e) => setCallTagFilter(e.target.value)}
            className="h-9 rounded-full border border-gray-200 bg-white pl-3 pr-8 text-xs font-medium text-gray-700 shadow-sm transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1rem] bg-[right_0.5rem_center] bg-no-repeat"
          >
            <option value="ALL">All ({callTagCounts.ALL})</option>
            {CALL_TAG_OPTIONS.map((tag) => (
              <option key={tag} value={tag}>
                {CALL_TAG_CONFIG[tag].label} ({callTagCounts[tag] || 0})
              </option>
            ))}
            {callTagCounts.UNTAGGED > 0 && (
              <option value="UNTAGGED">
                {UNTAGGED_CONFIG.label} ({callTagCounts.UNTAGGED})
              </option>
            )}
          </select>
        </div>

      </div>

      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <svg
            className="h-5 w-5 flex-shrink-0 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          {error}
        </div>
      )}

      {statusError && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <svg
            className="h-5 w-5 flex-shrink-0 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          {statusError}
        </div>
      )}

      {callTagError && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <svg
            className="h-5 w-5 flex-shrink-0 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          {callTagError}
        </div>
      )}

      {typeof total === "number" && (
        <p className="mb-2 text-xs font-medium text-gray-500" aria-live="polite">
          Showing {rows.length} of {total} calls
        </p>
      )}

      <div
        ref={scrollRootRef}
        className="flex-1 min-h-0 min-w-0 rounded-2xl border border-gray-200/60 bg-white shadow-lg shadow-gray-200/50 overflow-x-auto overflow-y-auto"
      >
        {loading ? (
          <div className="flex h-full min-h-[300px] items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-4 border-gray-200"></div>
                <div className="absolute top-0 left-0 h-12 w-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
              </div>
              <span className="text-sm font-medium text-gray-600">
                Loading calls...
              </span>
            </div>
          </div>
        ) : (
          <div>
            <table className="w-full">
              {/* ✅ Always render headers (even when there are 0 rows) */}
              <thead className="bg-gray-50/80 sticky top-0 z-10 backdrop-blur-sm">
                <tr>
                  {columns.map((column) => {
                    const isSortable = column.key === "created_at";
                    // Define column widths - reason_for_visit has no constraint to show full content
                    const columnWidths = {
                      phone_number: "w-[130px]",
                      callback_phone_number: "w-[130px]",
                      patient_name: "w-[120px]",
                      session_type: "w-[100px]",
                      insurance_provider: "w-[100px]",
                      email: "w-[140px]",
                      date: "w-[140px]",
                      created_at: "w-[120px]",
                      recording: "w-[180px]",
                      outbound: "w-[140px]",
                      rating: "w-[90px]",
                      edit: "w-[80px]",
                      elevenlabs: "w-[170px]",
                      action_notes: "w-[90px]",
                      status: "w-[100px]",
                      call_tag: "w-[140px]",
                      source: "w-[120px]",
                    };
                    const widthClass = columnWidths[column.key] || "";
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        onClick={isSortable ? toggleSort : undefined}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200/80 whitespace-nowrap first:pl-5 last:pr-5 ${widthClass} ${
                          isSortable
                            ? "cursor-pointer hover:bg-gray-100 select-none"
                            : ""
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.label}
                          {isSortable && (
                            <svg
                              className={`h-3 w-3 transition-transform ${
                                sortDirection === "asc" ? "rotate-180" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {hasData ? (
                  filteredRows.map((appt, index) => {
                    const rowId = getRowId(appt) || index;
                    const isEven = index % 2 === 0;
                    const isClinicOwnNumber =
                      isAdmin &&
                      isSamePhone(appt?.phone_number, selectedClinicPhone);

                    return (
                      <tr
                        key={rowId}
                        title={
                          isClinicOwnNumber
                            ? "Caller number matches the clinic's own phone number"
                            : undefined
                        }
                        className={`group transition-all duration-200 ${
                          isClinicOwnNumber
                            ? "bg-red-50 hover:bg-red-100/70"
                            : `hover:bg-blue-50/50 ${
                                expandedSummaryRowId === rowId
                                  ? "bg-gray-50/80"
                                  : isEven
                                    ? "bg-white"
                                    : "bg-gray-50/30"
                              }`
                        }`}
                      >
                        {columns.map((column) => {
                          if (column.isRecording) {
                            const hasRecording = !!appt.has_recording;
                            const pending = !!appt.recording_pending;
                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                {hasRecording ? (
                                  <AudioPlayButton
                                    conversationId={appt.conversation_id}
                                  />
                                ) : (
                                  <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                                    <span
                                      className={`h-2 w-2 rounded-full ${pending ? "bg-amber-400" : "bg-gray-300"}`}
                                    />
                                    {pending ? "Processing…" : "No recording"}
                                  </span>
                                )}
                              </td>
                            );
                          }

                          if (column.isActionNotes) {
                            const notes = appt.action_notes || "";
                            const hasNotes = notes.trim().length > 0;

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <button
                                  type="button"
                                  onClick={() => handleOpenNotesModal(appt)}
                                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
                                    hasNotes
                                      ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                  }`}
                                >
                                  <svg
                                    className={`h-4 w-4 flex-shrink-0 ${
                                      hasNotes
                                        ? "text-indigo-500"
                                        : "text-gray-400"
                                    }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d={
                                        hasNotes
                                          ? "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                          : "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                      }
                                    />
                                  </svg>
                                  <span className="truncate max-w-[50px]">
                                    {hasNotes ? notes : "Add"}
                                  </span>
                                </button>
                              </td>
                            );
                          }

                          if (column.isCallTag) {
                            const tagRaw = appt.call_tag
                              ? String(appt.call_tag).toUpperCase()
                              : null;
                            const tagConfig = getCallTagConfig(tagRaw);
                            const isOpen = openCallTagMenuId === rowId;
                            const isUpdating = updatingCallTagId === rowId;

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <div className="relative inline-block text-left">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenCallTagMenuId(
                                        isOpen ? null : rowId,
                                      )
                                    }
                                    disabled={isUpdating}
                                    className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 text-sm font-medium whitespace-nowrap transition-all duration-200 h-7 min-w-[110px] hover:shadow-sm cursor-pointer ${tagConfig.classes}`}
                                  >
                                    <span
                                      className={`h-2 w-2 rounded-full ${tagConfig.dot}`}
                                    ></span>
                                    <span>{tagConfig.label}</span>
                                  </button>

                                  {isOpen && (
                                    <div className="absolute right-0 z-20 mt-2 w-52 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none overflow-hidden">
                                      <div className="py-2">
                                        {CALL_TAG_OPTIONS.map((option) => {
                                          const optionConfig =
                                            CALL_TAG_CONFIG[option] ||
                                            UNTAGGED_CONFIG;
                                          return (
                                            <button
                                              key={option}
                                              type="button"
                                              onClick={() =>
                                                handleCallTagChange(
                                                  appt,
                                                  option,
                                                )
                                              }
                                              className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors duration-150 hover:bg-gray-50 ${
                                                option === tagRaw
                                                  ? "font-medium bg-gray-50"
                                                  : "text-gray-700"
                                              }`}
                                            >
                                              <span
                                                className={`h-2.5 w-2.5 rounded-full ${optionConfig.dot}`}
                                              ></span>
                                              {optionConfig.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          if (column.isStatus) {
                            const status = (
                              appt.status || "PENDING"
                            ).toUpperCase();
                            const isOpen = openMenuId === rowId;
                            const isUpdating = updatingId === rowId;

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <div className="relative inline-block text-left">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenMenuId(isOpen ? null : rowId)
                                    }
                                    disabled={isUpdating}
                                    className={getStatusClasses(status)}
                                  >
                                    <span
                                      className={`h-2 w-2 rounded-full ${getStatusDot(status)}`}
                                    ></span>
                                    <span>{getStatusLabel(status)}</span>
                                  </button>

                                  {isOpen && (
                                    <div className="absolute right-0 z-20 mt-2 w-44 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none overflow-hidden">
                                      <div className="py-2">
                                        {STATUS_OPTIONS.map((option) => {
                                          const optionConfig =
                                            STATUS_CONFIG[option] ||
                                            STATUS_CONFIG.NEW_CALL;
                                          return (
                                            <button
                                              key={option}
                                              type="button"
                                              onClick={() =>
                                                handleStatusChange(appt, option)
                                              }
                                              className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors duration-150 hover:bg-gray-50 ${
                                                option === status
                                                  ? "font-medium bg-gray-50"
                                                  : "text-gray-700"
                                              }`}
                                            >
                                              <span
                                                className={`h-2.5 w-2.5 rounded-full ${optionConfig.dot}`}
                                              ></span>
                                              {getStatusLabel(option)}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          // Rating column (admin only)
                          if (column.isRating) {
                            const hasConversationId = !!appt.conversation_id;
                            const ratingScore = appt.rating_score;
                            const hasRating = ratingScore != null;
                            const ratingPercent = hasRating
                              ? Math.round((ratingScore / 5) * 100)
                              : null;

                            // Color based on score
                            const getRatingColors = (score) => {
                              if (score >= 4)
                                return "bg-green-100 text-green-700 hover:bg-green-200";
                              if (score >= 3)
                                return "bg-yellow-100 text-yellow-700 hover:bg-yellow-200";
                              return "bg-red-100 text-red-700 hover:bg-red-200";
                            };

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                {hasConversationId ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenRatingModal(appt)}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
                                      hasRating
                                        ? getRatingColors(ratingScore)
                                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                    }`}
                                  >
                                    <svg
                                      className="h-4 w-4"
                                      fill={hasRating ? "currentColor" : "none"}
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={hasRating ? 0 : 2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                                      />
                                    </svg>
                                    {hasRating ? `${ratingPercent}%` : "Pending"}
                                  </button>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </td>
                            );
                          }

                          // Edit column (admin only)
                          if (column.isEdit) {
                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditModal(appt)}
                                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all duration-200"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
                                    />
                                  </svg>
                                  Edit
                                </button>
                              </td>
                            );
                          }

                          // ElevenLabs conversation deep link (admin only)
                          if (column.isElevenLabsLink) {
                            const url = appt.elevenlabs_conversation_url;

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open this conversation in ElevenLabs"
                                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 transition-all duration-200 whitespace-nowrap"
                                  >
                                    <svg
                                      className="h-4 w-4 flex-shrink-0"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                      />
                                    </svg>
                                    Show in ElevenLabs
                                  </a>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </td>
                            );
                          }

                          // Source badge column (outreach provenance)
                          if (column.isSource) {
                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                {isOutreachBooking(appt) ? (
                                  <span
                                    title="Booked by outreach"
                                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-teal-100 text-teal-800"
                                  >
                                    <span className="h-2 w-2 rounded-full bg-teal-500" />
                                    Outreach
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </td>
                            );
                          }

                          // Outbound call column
                          if (column.isOutbound) {
                            const hasOutcome = !!appt.outbound_call_outcome;
                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <div className="flex items-center gap-2">
                                  {hasOutcome && (
                                    <CallOutcomeInline
                                      outcome={appt.outbound_call_outcome}
                                      outcomeLabel={appt.outbound_call_outcome_label}
                                      onClick={appt.outbound_call_id ? () => {
                                        setSummaryCallId(appt.outbound_call_id);
                                        setSummaryAppointment(appt);
                                        setSummaryModalOpen(true);
                                      } : undefined}
                                    />
                                  )}
                                  <OutboundCallButton
                                    appointment={appt}
                                    onCallInitiated={(callData) =>
                                      patchRow(rowId, {
                                        outbound_call_id: callData.id,
                                        last_outbound_call_at:
                                          callData.call_initiated_at,
                                      })
                                    }
                                    onCallStatusChange={(statusData) =>
                                      patchRow(rowId, {
                                        outbound_call_outcome:
                                          statusData.outbound_call_outcome,
                                      })
                                    }
                                  />
                                </div>
                              </td>
                            );
                          }

                          // Special rendering for name column
                          if (column.key === "patient_name") {
                            const name = toDisplayText(appt?.[column.key]);
                            const displayName = name || "Unknown";

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <span className="font-medium text-gray-900 text-sm">
                                  {displayName}
                                </span>
                              </td>
                            );
                          }

                          // Special rendering for phone column
                          if (
                            column.key === "phone_number" ||
                            column.key === "callback_phone_number"
                          ) {
                            const phone = appt?.[column.key];
                            const displayPhone = formatPhoneDisplay(phone);

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle first:pl-5 last:pr-5"
                              >
                                <span className="font-mono text-sm text-gray-700">
                                  {displayPhone}
                                </span>
                              </td>
                            );
                          }

                          // Special rendering for Preferred Date column
                          if (column.key === "date") {
                            const dateValue = appt?.date;
                            const formattedDate = formatPreferredDate(dateValue);

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-middle text-gray-600 first:pl-5 last:pr-5 text-sm whitespace-nowrap"
                              >
                                {formattedDate}
                              </td>
                            );
                          }

                          // Custom rendering for Call Summary (reason_for_visit)
                          if (column.key === "reason_for_visit") {
                            const summaryText = toDisplayText(
                              appt?.reason_for_visit,
                            );
                            const isEmpty = summaryText.length === 0;
                            const isExpanded = expandedSummaryRowId === rowId;
                            const needsTruncation = !isEmpty && summaryText.length > 120;

                            if (isEmpty) {
                              return (
                                <td
                                  key={`${column.key}-${rowId}`}
                                  className="px-4 py-3 align-middle text-gray-600 first:pl-5 last:pr-5 text-sm"
                                >
                                  —
                                </td>
                              );
                            }

                            return (
                              <td
                                key={`${column.key}-${rowId}`}
                                className="px-4 py-3 align-top text-gray-600 first:pl-5 last:pr-5 text-sm"
                              >
                                {isExpanded ? (
                                  // Expanded state
                                  <div>
                                    <div className="bg-blue-50 border-l-[3px] border-blue-500 rounded-md p-3 text-gray-700 leading-relaxed">
                                      {summaryText}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setExpandedSummaryRowId(null)}
                                      className="mt-2 text-blue-500 hover:text-blue-700 text-xs font-medium"
                                    >
                                      Show less
                                    </button>
                                  </div>
                                ) : (
                                  // Collapsed state
                                  <div>
                                    <div
                                      className={
                                        needsTruncation
                                          ? "line-clamp-2 overflow-hidden"
                                          : ""
                                      }
                                    >
                                      {summaryText}
                                    </div>
                                    {needsTruncation && (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedSummaryRowId(rowId)}
                                        className="mt-1 text-blue-500 hover:text-blue-700 text-xs font-medium"
                                      >
                                        Show more
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          }

                          let cellValue = appt?.[column.key];

                          if (/_at$/i.test(column.key)) {
                            cellValue = formatDateTime(toDisplayText(cellValue));
                          } else {
                            cellValue = toDisplayText(cellValue) || "—";
                          }

                          return (
                            <td
                              key={`${column.key}-${rowId}`}
                              className="px-4 py-3 align-middle text-gray-600 first:pl-5 last:pr-5 text-sm"
                            >
                              {cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  // ✅ Empty table row message while keeping headers visible
                  <tr>
                    <td
                      colSpan={columns.length || 1}
                      className="px-6 py-20 text-center"
                    >
                      {isFiltered ? (
                        // Filtered but no results
                        <div className="flex flex-col items-center gap-4">
                          <div className="rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 p-6 shadow-inner">
                            <svg
                              className="h-12 w-12 text-amber-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                          <div className="space-y-2">
                            <p className="text-lg font-semibold text-gray-900">
                              No matching calls
                            </p>
                            <p className="text-sm text-gray-500 max-w-xs mx-auto">
                              Try adjusting your search or filters to find what
                              you're looking for.
                            </p>
                            <button
                              onClick={clearFilters}
                              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                              Clear filters
                            </button>
                          </div>
                        </div>
                      ) : (
                        // No data at all
                        <div className="flex flex-col items-center gap-4">
                          <div className="rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 p-6 shadow-inner">
                            <svg
                              className="h-12 w-12 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                              />
                            </svg>
                          </div>
                          <div className="space-y-1">
                            <p className="text-lg font-semibold text-gray-900">
                              No calls yet
                            </p>
                            <p className="text-sm text-gray-500 max-w-xs mx-auto">
                              When new frontdesk calls come in, they'll appear
                              here.
                            </p>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}

                {/* Paging: the last row loads the next page as it comes into
                    view, says so while that happens, and says when there is
                    nothing more. A table without paging renders none of it. */}
                {hasData && (loadingMore || hasMore || sentinelRef) && (
                  <tr ref={sentinelRef} data-testid="calls-sentinel">
                    <td
                      colSpan={columns.length || 1}
                      className="relative px-6 py-4 text-center text-xs font-medium text-gray-500"
                    >
                      {loadingMore ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                          Loading more…
                        </span>
                      ) : !hasMore ? (
                        <span>All {typeof total === "number" ? total : rows.length} calls loaded</span>
                      ) : (
                        <button
                          type="button"
                          className="sr-only"
                          onClick={onLoadMore}
                        >
                          Load more
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActionNotesModal
        isOpen={notesModalOpen}
        onClose={handleCloseNotesModal}
        appointment={selectedAppointment}
        onSave={handleSaveNotes}
        isSaving={savingNotes}
        error={notesError}
        campaignDecline={campaignDecline}
        onUndoCampaignDecline={handleUndoCampaignDecline}
        isUndoing={undoingDecline}
      />

      {/* Rating Modal (admin only) */}
      {isAdmin && (
        <CallRatingModal
          isOpen={ratingModalOpen}
          onClose={handleCloseRatingModal}
          conversationId={ratingModalData.conversationId}
          patientName={ratingModalData.patientName}
        />
      )}

      {/* Edit Appointment Modal (admin only) */}
      {isAdmin && (
        <EditAppointmentModal
          isOpen={editModalOpen}
          onClose={handleCloseEditModal}
          appointment={editAppointment}
          onSave={handleSaveEdit}
          isSaving={savingEdit}
          error={editError}
        />
      )}

      {/* Call Summary Modal */}
      <CallSummaryModal
        isOpen={summaryModalOpen}
        onClose={() => {
          setSummaryModalOpen(false);
          setSummaryCallId(null);
          setSummaryAppointment(null);
        }}
        outboundCallId={summaryCallId}
        appointmentData={summaryAppointment}
      />
    </section>
  );
}
